import React, { FC } from 'react';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import {
  isCiaCategory,
  ModelFilterCategory,
  ModelFilterState,
  useModelFilters,
} from '@store/zustand/model-filters';
import {
  CATEGORY_LABELS,
  CategoryOption,
} from '@views/Project/components/ModelFiltersPopover/categories';
import FilterPanelBackTitle from '@views/Project/components/ModelFiltersPopover/components/FilterPanelBackTitle';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';
import FilterValueRow from '@views/Project/components/ModelFiltersPopover/components/FilterValueRow';
import { useSetCategory } from '@views/Project/components/ModelFiltersPopover/useSetCategory';

type Props = {
  category: ModelFilterCategory;
  options: CategoryOption[];
  counts?: Record<string, number>;
  criteriaId?: string;
  title?: string;
  onBack?: () => void;
};

/**
 * The values ticked in the list this panel shows: the flat category's list,
 * or one criteria's answers for a CIA category. A CIA category with no
 * criteria has no list to show, so it reads as empty.
 */
const selectionOf = (
  state: ModelFilterState,
  category: ModelFilterCategory,
  criteriaId: string | undefined,
): string[] => {
  if (!isCiaCategory(category)) {
    return state[category];
  }

  return criteriaId ? state.ciaFilters[category][criteriaId] ?? [] : [];
};

/**
 * One category's value list, rendered inside the popover in place of the
 * root or beside a criteria list.
 *
 * Every toggle writes straight through to the store and the panel stays open:
 * there is no Confirm button anywhere in the popover (AC4.3). The write goes
 * through `useSetCategory`, so a tick that would hide the entity an open
 * drawer shows closes that drawer first. "Clear all"
 * appears only while this list has a selection and clears this list alone
 * (AC4.1.2): for a CIA category that is the one criteria named by
 * `criteriaId`, never its sibling criteria, so the header and the rows always
 * describe the same values. A CIA category must therefore arrive with the
 * `criteriaId` whose answers `options` lists; without it the store ignores
 * the toggles. The header names the category unless `title` says otherwise;
 * the container passes the criteria's name for a CIA category, whether the
 * category opened straight onto its only criteria or this list is the side
 * panel of a criteria list, so the header names the list the rows show. As
 * that side panel its back closes it and leaves the list showing, the same
 * as a click on the open row; the list's own back drops it with the list.
 * Counts are rendered only when the container supplies them, and a value
 * missing from the supplied map reads 0 rather than losing its pill
 * (AC5.1.1); the container withholds the whole map once anything is
 * selected anywhere, which is AC5.2.
 */
const FilterCategoryPanel: FC<Props> = ({
  category,
  options,
  counts,
  criteriaId,
  title,
  onBack,
}) => {
  const state = useModelFilters();
  const setCategory = useSetCategory();

  const selected = selectionOf(state, category, criteriaId);

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];

    setCategory(category, next, criteriaId);
  };

  const clear = () => setCategory(category, [], criteriaId);

  return (
    <FilterPanelLayout
      title={
        <FilterPanelBackTitle
          title={title ?? CATEGORY_LABELS[category]}
          onBack={onBack}
        />
      }
      action={
        selected.length > 0 && (
          <UnderlineTextButton
            $disabled={false}
            onClick={clear}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      {options.map(({ value, label }) => (
        <FilterValueRow
          key={value}
          label={label}
          checked={selected.includes(value)}
          count={counts ? counts[value] ?? 0 : undefined}
          onToggle={() => toggle(value)}
        />
      ))}
    </FilterPanelLayout>
  );
};

export default FilterCategoryPanel;
